import React, { useEffect, useState, useRef } from "react"
import { getPersonalityData } from "services/break.js"

import DropdownSection from "./parts/DropdownSection"
import ColumnsSection from "./parts/ColumnsSection"
import ReferralCardScreen from "./components/ReferralCardScreen"
import ShareIcons from "components/ShareIcons"
import WistiaEmbed from "steps/video/WistiaEmbed"

import { createTheme, ThemeProvider } from "@material-ui/core/styles"
import { fontFamily } from "global/_js-variables"

import "./ReferralStep.scss"
import LegacyRetinaImage from "components/LegacyRetinaImage"
import { useIntersectionObserver } from "hooks/useIntersectionObserver"

import analytics from "global/AnalyticsData"


/*
This version of the referral step uses the new "step3" logic. (Feb 2021).
*/

const ReferralStep = ({ brk, breakId, step, onEngage = function() {}, onAwardPoints = function() {}, onAuthRequired, onUnhandledError, onServerWaiting = function() {}, skipButton, nextStepButton }) => {
  const [pqResultId, setPqResultId] = useState(undefined)
  const hasVote = step.links.some((link) => link.hasGoal) || (step.referralData.voteType === "ALL_ITEMS") || (step.referralData.voteType === "PER_ITEM")

  //state funcs converted to ref funcs
  const sectionEl = useRef(null)

  let ReferralObject = step.referralData
  ReferralObject.links = step.links
  const layout = ReferralObject.layout
  const dropdownLinks = ReferralObject.links.filter(link => link.buttonType === "MENU")
  const buttonLinks = ReferralObject.links.filter(link => link.buttonType === "BUTTON")

  const [inView, entry] = useIntersectionObserver(sectionEl, {
    threshold: 0.1,
  })


  useEffect(() => {
    const asyncWrapper = async () => {
      if (inView && pqResultId === undefined) {
        await getPersonalityQuizResultId()
      }
    }
    asyncWrapper()
  }, [inView])

  const getPersonalityQuizResultId = async () => {
    try {
      const response = await getPersonalityData(breakId)
      //   console.log(response)
      setPqResultId(response.detail.resultId)
    } catch (e) {
      console.log(e)
      if (e.message === "401") {
        onAuthRequired && onAuthRequired()
      } else {
        onUnhandledError && onUnhandledError()
      }
      setPqResultId(null)
    }
  }

  const closeDropdowns = e => {
    const openDropdowns = [...document.getElementsByClassName("dropdown-cover open")]
    if (!e.target.matches(".dropdown-card *")) {
      openDropdowns.length && openDropdowns.forEach(dropdown => dropdown.classList.remove("open"))
    }
  }

  const setInteractedAndUnlock = () => {
    onEngage()
    onAwardPoints()
  }

  const handleLinkClick = link => {
    if (typeof window !== `undefined`) {
      window.dataLayer.push({
        event: "step_interaction_referral_click",
      })

      analytics.stepEvent("step_interaction_referral_click", step, {
        result_id: link.id,
        result: link.url,
      })
    }

    setInteractedAndUnlock()
  }

  const handleShareClick = ({platform}) => {
    analytics.stepEvent("step_interaction_referral_share_option", step, {
      result_id: platform,
      result: platform,
    })
  }

  const handleVideoComponentLoad = () => {
    console.log(`handleVideoComponentLoad`)
  }

  const handleVideoViewed = () => {
    console.log(`handleVideoViewed`)
  }

  const handleVideoPlayed = (videoId, videoName) => {
    console.log(`handleVideoPlayed`)
  }

  const handleVideoWatched = (videoId, videoName) => {
    console.log(`handleVideoWatched`)
  }

  const referralTheme = createTheme({
    typography: {
      fontFamily: fontFamily
    }
  })

  return (
    <ThemeProvider theme={referralTheme}>
    <section ref={sectionEl} className={`referral-step continue-ready waiting-false `}>
      <div className="step-content with-shadow" onClick={closeDropdowns.bind(this)}>
        {ReferralObject.headline && <h1 dangerouslySetInnerHTML={{ __html: ReferralObject.headline }} />}
        {/* To do: simplify and remove this missioncolor use her so we don't need to pass a brk prop */}
        {ReferralObject.introCopy && <p style={{ color: brk.missionColor6 }} dangerouslySetInnerHTML={{ __html: ReferralObject.introCopy }}></p>}

        { ReferralObject && ReferralObject.videoId &&
          <div className="referral-video">
            <div className="wistia_responsive_wrapper">
              <WistiaEmbed onLoaded={handleVideoComponentLoad} videoIDs={[ReferralObject.videoId]} handleVideoViewed={handleVideoViewed} handleVideoPlayed={handleVideoPlayed} handleVideoWatched={handleVideoWatched} autoPlay={false} />
            </div>
          </div>
        }

        {layout === "ONE_COLUMN" && (
          <div className="referral-body">
            <div className="referral-image-single">
              {/* TODO - smelly: either use better validation or change the backend */}
              {ReferralObject.links[0].url && ReferralObject.links[0].url !== "https://" ? (
                <a href={ReferralObject.links[0].url} target="_blank" rel="noopener noreferrer" onClick={handleLinkClick}> 
                  <LegacyRetinaImage image={ReferralObject.headerImage} />
                </a>
              ) : (
                <LegacyRetinaImage image={ReferralObject.headerImage} />
              )}
            </div>
            <div className={`combined-section`}>
              {dropdownLinks.length > 0 && <DropdownSection dropdownLinks={dropdownLinks} layout={layout} pqResultId={pqResultId} setInteractedAndUnlock={setInteractedAndUnlock} step={step} />}
              {buttonLinks.length > 0 && <ColumnsSection buttonLinks={buttonLinks} layout={layout} pqResultId={pqResultId} onLinkClick={handleLinkClick} />}
            </div>
          </div>
        )}

        {layout !== "ONE_COLUMN" && (
          <div className="referral-body">
            {dropdownLinks.length > 0 && (
              <div className={`dropdown-container${layout ? " " + layout : ""}`}>
                <DropdownSection dropdownLinks={dropdownLinks} pqResultId={pqResultId} setInteractedAndUnlock={setInteractedAndUnlock} step={step} />
              </div>
            )}
            {!hasVote && buttonLinks.length > 0 && (
              <div className={`column-container${layout ? " " + layout : ""}`}>
                <ColumnsSection buttonLinks={buttonLinks} layout={layout} pqResultId={pqResultId} onLinkClick={handleLinkClick} />
              </div>
            )}
            {hasVote && buttonLinks.length > 0 && 
              <ReferralCardScreen 
                referralLinks={buttonLinks} 
                referralId={step.referralData.id} 
                onLinkClick={handleLinkClick} 
                layout={layout} 
                voteType={step.referralData.voteType} />
            }

          </div>
        )}
        <div className="referral-bottom-text" dangerouslySetInnerHTML={{ __html: ReferralObject.bottomText }}></div>
        {hasVote && step.socialShareOn && <ShareIcons
														facebookShare={step.facebookShare}
														twitterShare={step.twitterShare}
														pinterestShare={step.pinterestShare}
														emailShare={step.emailShare}
														copyShare={step.copyShare}
														whatsappShare={step.whatsappShare}
                            shareUrl={brk ? brk.shareUrl : ""}
                            onShareClick={handleShareClick}
													/>}
        {skipButton}
        {nextStepButton}
      </div>
      
     
    </section>
    </ThemeProvider>
  )
}

export default ReferralStep
